import { useCallback } from "react";

import { useMutation, useQuery } from "@apollo/client/react";

import {
  DraftParagraphEditDocument,
  DraftSectionLineEditDocument,
  ProjectDraftParagraphDocument,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { type ParagraphRun } from "@styled/paragraph_editor_runs";

import { type DraftFill } from "./draft_fill";
import { type DraftingSections } from "./use_drafting_sections";
import { useParagraphEditing } from "./use_paragraph_editing";

// In the window; `room` is the space over it, up to the text above it.
export type EditSpot = {
  top: number;
  left: number;
  width: number;
  room: number;
};

// Live editing: his paragraph, read fresh and saved on Done against the file version he saw; or a
// drafted line as the Report fills it (`fills`), saved against the line he saw. His Report then redraws.
export const useDraftParagraphEdit = (
  projectId: string,
  drafting: DraftingSections,
  chat: { conversationId?: string; fills: ReadonlyArray<DraftFill> },
) => {
  const [saveParagraph, paragraphState] = useMutation(
    DraftParagraphEditDocument,
    { refetchQueries: ["ProjectDraftingSections"], awaitRefetchQueries: true },
  );
  const [saveLine, lineState] = useMutation(DraftSectionLineEditDocument, {
    refetchQueries: drafting.rechecked,
    awaitRefetchQueries: true,
  });
  const { reset: resetParagraph } = paragraphState;
  const { reset: resetLine } = lineState;
  const reset = useCallback(() => {
    resetParagraph();
    resetLine();
  }, [resetParagraph, resetLine]);
  const { done, ...state } = useParagraphEditing(reset);
  const { editing } = state;
  const block = editing && "block" in editing ? editing.block : null;
  const { data } = useQuery(ProjectDraftParagraphDocument, {
    variables: {
      id: projectId,
      documentId: drafting.draftId ?? "",
      block: block ?? 0,
    },
    skip: block === null || !drafting.draftId,
    fetchPolicy: "network-only",
  });
  const paragraph = data?.project.document.draftParagraph;
  const current = paragraph?.block === block ? paragraph : undefined;
  const line =
    editing && "line" in editing
      ? chat.fills.find((fill) => fill.marker === editing.marker)?.lines[
          editing.line
        ]
      : undefined;

  // The runs he opened, as the editor shows them.
  const shown =
    (line ? line.runs : current && !current.lock && current.runs) || undefined;

  const counted = (changed: boolean | undefined, target: "draft" | "line") => {
    if (changed) {
      track("drafting_paragraph_edited", { project_id: projectId, target });
    }
    return changed;
  };

  const save = (runs: ParagraphRun[]) => {
    if (!editing) return;
    if ("block" in editing) {
      if (!drafting.draftId || drafting.fileVersion == null) return;
      return saveParagraph({
        variables: {
          id: projectId,
          input: {
            documentId: drafting.draftId,
            block: editing.block,
            fileVersion: drafting.fileVersion,
            runs,
          },
        },
      }).then(({ data }) =>
        counted(data?.project.draftParagraphEdit.changed, "draft"),
      );
    }
    const section = drafting.sections.find(
      ({ marker }) => marker === editing.marker,
    );
    if (!section || !line) return;
    return saveLine({
      variables: {
        id: projectId,
        input: {
          sectionId: section.id,
          conversationId: chat.conversationId,
          line: editing.line,
          seen: line.runs.map(({ text, bold, italic, underline }) => ({
            text,
            bold,
            italic,
            underline,
          })),
          runs,
        },
      },
    }).then(({ data }) =>
      counted(data?.project.draftSectionLineEdit.changed, "line"),
    );
  };

  return {
    ...state,
    runs: shown,
    saving: paragraphState.loading || lineState.loading,
    error: (paragraphState.error ?? lineState.error)?.message,
    done: (runs: ParagraphRun[]) => done(shown, runs, () => save(runs)),
  };
};

export type DraftParagraphEdit = ReturnType<typeof useDraftParagraphEdit>;
